import React, { useRef, useState } from "react";
import { CheckCircle2, Award, Quote } from "lucide-react";
import atrp from "../assets/cred1.png";
import ceBodyLiberation from "../assets/cred3.png";
import ceTherapistDialectics from "../assets/cred4.png";
import ncc from "../assets/certs/ncc.jpg";
import lpcKansas from "../assets/certs/lpc-kansas.jpg";
import psiChi from "../assets/certs/psi-chi.jpg";
import naloxone from "../assets/certs/naloxone.jpg";
import ceArtOfDisruption from "../assets/certs/ce-art-of-disruption.jpg";
import cePostnormal from "../assets/certs/ce-postnormal-possibilities.jpg";

import PORTRAIT from "../assets/jasmine-red-backdrop.jpg";
import { Disclosure, ReadMore } from "../components/Disclosure";
import Gallery from "../components/Gallery";
import "./AboutMore.css";

/** Her "Areas of Clinical Interest" (round 2, Nov 2026), word for word. */
const CLINICAL_INTERESTS = [
  [
    "Children and behavioral concerns",
    "Working collaboratively with children and parents to understand behavioral needs, establish healthy boundaries, use supportive and consistent consequences, and build practical emotional tools that children can learn and practice.",
  ],
  [
    "Children with autism",
    "In-person art therapy that supports creative expression and provides opportunities for communication and connection, including for children who are nonspeaking or communicate without spoken language.",
  ],
  [
    "College students",
    "Navigating identity, relationships, academic pressures, and life transitions.",
  ],
  [
    "Individuals returning to the community after incarceration",
    "Navigating the emotional and relational challenges of reintegration.",
  ],
  [
    "Addiction",
    "Exploring the emotional experiences, unmet needs, and underlying pain that may contribute to addictive patterns. My approach is informed by the work of Dr. Gabor Maté and his emphasis on understanding addiction through compassion, curiosity, and the broader experiences of the individual.",
  ],
  [
    "Pregnancy and postpartum mental health",
    "Creating space to prioritize emotional wellbeing during pregnancy and after childbirth.",
  ],
];

const CERT_IMAGES = [
  {
    src: atrp,
    alt: "Art Therapy Credentials Board (ATCB) certificate granting Jasmine Godina Smith the ATR-Provisional designation, issued July 2025",
  },
  {
    src: lpcKansas,
    alt: "State of Kansas Behavioral Sciences Regulatory Board licence for Jasmine Godina Smith as a Professional Counselor, licence LPC 05095, July 1, 2025",
  },
  {
    src: ncc,
    alt: "National Board for Certified Counselors certificate recognising Jasmine Smith as a National Certified Counselor (NCC), 2025 to 2030",
  },
  {
    src: psiChi,
    alt: "Psi Chi International Honor Society in Psychology certificate of lifetime membership for Jasmine Smith, inducted at Emporia State University in 2023",
  },
  {
    src: naloxone,
    alt: "Emporia State University Naloxone Training Certified badge issued to Jasmine Smith, January 30, 2024",
  },
  {
    src: ceArtOfDisruption,
    alt: "Continuing education certificate of completion, Expressing Pride 2025 symposium: The Art of Disruption, Queer and Crip Inquiry in Creative Arts Therapy, June 21, 2025",
  },
  {
    src: cePostnormal,
    alt: "Continuing education certificate of completion, Expressing Pride 2025 symposium: Postnormal Possibilities, Shifting the Cultures of Practice in Creative Arts Therapies, June 21, 2025",
  },
  {
    src: ceBodyLiberation,
    alt: "Continuing education certificate of completion, Expressing Pride 2025 symposium: Body Liberation for Trans and Gender Expansive Folks, June 21, 2025",
  },
  {
    src: ceTherapistDialectics,
    alt: "Continuing education certificate of completion, Expressing Pride 2025 symposium: Therapist Dialectics, Caring for Ourselves when Caring for our Clients, June 21, 2025",
  },
];

export default function AboutMore() {
  const [idx, setIdx] = useState(0);
  const trackRef = useRef(null);

  const next = () => setIdx((i) => (i + 1) % CERT_IMAGES.length);
  const prev = () => setIdx((i) => (i - 1 + CERT_IMAGES.length) % CERT_IMAGES.length);
  const go = (i) => setIdx(i);

  // Swiper
  const startX = useRef(null);
  const onTouchStart = (e) => (startX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx > 40) prev();
    if (dx < -40) next();
    startX.current = null;
  };

  return (
    <section className="section white">
      <div className="container">
        <div className="about-hero">
          <div className="profile-card">
            <div className="profile-img-wrap">
              <img
                src={PORTRAIT}
                alt="Jasmine Smith in a floral dress in front of a red door"
                className="profile-img"
                width={1066}
                height={1600}
              />
            </div>
            <h1 className="name">Meet Jasmine G. Smith</h1>
            <p className="tagline">Your therapist · MS, ATR-P, LPC, NCC</p>
          </div>

          <div className="bio-card">
            <div className="quote">
              <Quote className="quote-icon" aria-hidden />
              <p>
                Healing is not about fixing, it’s about becoming. I’m honored to walk with you on your journey.
              </p>
            </div>

            {/* Her round-2 "Meet Jasmine" text (Nov 2026), word for word. The
                first paragraphs stay visible; the long sections open on tap,
                as she suggested ("a dropdown list for some of the headings"). */}
            <p className="bio"><strong>Welcome to Healing Hues, and thank you for stopping by!</strong></p>
            <p>
              Born and raised in New Providence, The Bahamas, creativity has been part of my life for as long as I can remember. Growing up surrounded by the vibrant artistry and cultural expression of Junkanoo shaped my appreciation for art as a way of expressing identity, telling stories, and bringing people together.
            </p>
            <p>
              My journey into art therapy was a personal one. After seeking therapy myself, I discovered art therapy and was drawn to the possibilities of healing through creativity. It felt deeply aligned with who I was and how I understood the world. With the support of my family and through faith, I completed my year of teaching, relocated, and committed to pursuing this path.
            </p>

            <div className="bio-sections">
              <Disclosure title="My Education and Professional Journey">
                <p>
                  At the University of The Bahamas, I completed an Associate of Arts and a Bachelor of Arts in Art Education concurrently. Before graduate school, my love for teaching art led me to C.C. Sweeting Senior High School and the National Art Gallery of The Bahamas’ Mixed Media Summer Camp, where I especially enjoyed working with children and exploring the ways creativity could support their expression.
                </p>
                <p>
                  This passion eventually led me to Emporia State University in Emporia, Kansas, where I earned two Master of Science degrees in Art Therapy Counseling and Clinical Mental Health Counseling. Both programs were CACREP-accredited.
                </p>
                <p>
                  My clinical experience spans several settings and populations, including in-person art therapy with children, individual counseling with college students in person and virtually, and virtual community therapy through Beyond Our Health for clients in The Bahamas. I have also provided virtual counseling to incarcerated individuals in local jails across several nearby Kansas counties.
                </p>
                <p>
                  I hold a Licensed Professional Counselor (LPC) license in Kansas, the National Certified Counselor (NCC) credential, and the Provisionally Registered Art Therapist (ATR-P) designation. My clinical approach is informed by person-centered and Gestalt principles, with an emphasis on emotional awareness, connection, and self-understanding. Clinical services are provided under the supervision of Dr. Jessica Stallings, ATR-BC, LIMHP, LPC, LMHC, as I continue working toward the full Art Therapist Registered (ATR) credential.
                </p>
              </Disclosure>

              <Disclosure title="Community, Research and Advocacy">
                <p>
                  Returning home to The Bahamas created opportunities to connect my professional interests with the community that shaped me. During graduate training, I completed a summer internship with the Bahamas Association for the Physically Disabled alongside classmates who travelled to The Bahamas for the experience. Other community experiences have included outreach through the National Art Gallery of The Bahamas, free art therapy activities at community events in Kansas and New Providence, and seminars at the University of The Bahamas, where I currently serve as an Adjunct Instructor.
                </p>
                <p>
                  Expanding access to creative mental health care is also central to the work of Healing Hues. Through free virtual art therapy groups, individuals and families across The Bahamas can participate regardless of which island they call home. Made Together creates opportunities for families to connect through shared art-making, Color Your Calm offers teens a space to explore friendship, connection, and belonging, and The Emotional Canvas invites adults to rediscover joy, creativity, and emotional expression. These groups reflect a continued commitment to making creative mental health experiences more accessible within our communities.
                </p>
                <p>
                  In 2023, I conducted research exploring the accessibility of art therapy across the Caribbean. The findings highlighted interest in art therapy and openness to telehealth, reinforcing the importance of creating more opportunities for people to access mental health support throughout the region.
                </p>
                <p>
                  That commitment extends to professional advocacy. Alongside an incredible group of colleagues, I am working to establish the Art Therapy Association of The Bahamas, serving as its Founding President. The goal is to support the growth, visibility, and development of art therapy in our country.
                </p>
                <p>
                  My professional affiliations include the American Art Therapy Association, Psi Chi International Honor Society, and Sigma Gamma Rho Sorority, Incorporated. In 2026, Healing Hues was nominated for the Social Impact Award at the National MSME Honors, marking a meaningful milestone in the practice’s development.
                </p>
              </Disclosure>

              <Disclosure title="Beyond the Therapy Room">
                <p>
                  Outside of clinical work, creativity remains an important part of my life. As an abstract artist, I especially appreciate that art therapy does not require artistic skill or perfection. Some of the most meaningful experiences happen when we become immersed in the process without worrying about what the final product will look like.
                </p>
                <p>
                  Whether painting, knitting, or creating graphics in Canva, I enjoy finding different ways to create. Beyond my professional life, I’m a wife and mother, a woman of faith, and someone who appreciates a good balance of creativity and downtime.
                </p>
                <p>
                  When you’re ready to begin, I’d be glad to walk alongside you as you explore, create, and grow.
                </p>
              </Disclosure>
            </div>

            <div className="practice-about">
              <h2>Healing Hues</h2>
              <p>
                Healing Hues is a therapeutic practice offering art therapy, talk therapy, and creative mental health programming for children, teenagers, and adults. Services are available online and in person, including home-based sessions where appropriate.
              </p>
              <ReadMore context="Healing Hues">
                <p>
                  At the heart of Healing Hues is the belief that understanding ourselves is an important part of caring for ourselves. I believe emotions are neither good nor bad; they offer valuable information about our needs, experiences, and values. Through therapy, we make space to approach emotions with curiosity rather than judgment, reconnect with our bodies, and develop practical tools to navigate life’s experiences.
                </p>
                <p>
                  My approach is informed by person-centered and Gestalt principles, with an emphasis on emotional awareness, connection, and reducing the sense of aloneness that can accompany difficult experiences. I value slowing down, sitting with our emotions, noticing moments of joy and safety, and making room for reflection and self-care.
                </p>
              </ReadMore>

              <div className="bio-sections">
                <Disclosure title="Areas of Clinical Interest" level={3}>
                  <p>My clinical interests include:</p>
                  <ul className="clinical-interests">
                    {CLINICAL_INTERESTS.map(([area, text]) => (
                      <li key={area}><strong>{area}:</strong> {text}</li>
                    ))}
                  </ul>
                </Disclosure>
              </div>

              <p>
                Beyond individual therapy, Healing Hues offers group experiences, workshops, psychoeducation, and creative mental health programming. Schools and organizations are welcome to enquire about contracting services for art therapy, emotional wellness education, and creative programming.
              </p>
            </div>

            <ul className="quals">
              <li><CheckCircle2 aria-hidden="true" /> Registered Art Therapist - Provisional (ATR-P)</li>
              <li><CheckCircle2 aria-hidden="true" /> Licensed Professional Counselor (LPC – Kansas)</li>
              <li><CheckCircle2 aria-hidden="true" /> National Certified Counselor (NCC)</li>
              <li><CheckCircle2 aria-hidden="true" /> M.S. in Art Therapy Counseling (CACREP-Accredited)</li>
              <li><CheckCircle2 aria-hidden="true" /> M.S. in Clinical Mental Health Counseling (CACREP-Accredited)</li>
            </ul>

            <div className="badges">
              <span><Award size={18} aria-hidden="true" /> Member, Psi Chi International Honor Society in Psychology</span>
              <span><Award size={18} aria-hidden="true" /> Member, American Art Therapy Association</span>
              <span><Award size={18} aria-hidden="true" /> Member, Sigma Gamma Rho Sorority, Incorporated</span>
            </div>
          </div>
        </div>

        <div className="certs-wrap">
          <h2 id="certs-heading">Certifications & Professional Development</h2>

          <div
            className="carousel"
            role="region"
            aria-roledescription="carousel"
            aria-labelledby="certs-heading"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <button type="button" className="car-btn left" onClick={prev} aria-label="Previous certificate">‹</button>

            <div className="car-viewport">
        <div
            className="car-track"
            ref={trackRef}
            style={{ transform: `translateX(calc(${idx} * -100%))` }}
            >
            {CERT_IMAGES.map((item, i) => (
            <div className="car-slide" key={i} aria-hidden={i !== idx}>
            <img src={item.src} alt={item.alt || `Certificate ${i + 1}`} />
      </div>
    ))}
  </div>
</div>


            <button type="button" className="car-btn right" onClick={next} aria-label="Next certificate">›</button>
          </div>

          <div className="car-dots">
            {CERT_IMAGES.map((_, i) => (
              <button
                type="button"
                key={i}
                className={`dot ${i === idx ? "active" : ""}`}
                onClick={() => go(i)}
                aria-label={`Go to certificate ${i + 1}`}
                aria-current={i === idx ? "true" : undefined}
              />
            ))}
          </div>
        </div>

        <Gallery album="about" />
      </div>

      {/* Decorative leaves */}
      <svg className="leaf leaf-about-1" viewBox="0 0 100 100" aria-hidden>
        <path d="M50 10C30 20 15 40 10 60c-3 12 0 20 10 20 25 0 52-13 66-38 8-14-2-36-36-32Z"/>
      </svg>
      <svg className="leaf leaf-about-2" viewBox="0 0 100 100" aria-hidden>
        <path d="M50 10C30 20 15 40 10 60c-3 12 0 20 10 20 25 0 52-13 66-38 8-14-2-36-36-32Z"/>
      </svg>
    </section>
  );
}
